/**
 * DailyClaimCard — odbierz dzisiejsze diamenty + pokaż streak.
 * Użyte w MyPortfolioPage. Po claim → confetti-toast i odświeżenie stats.
 */
import { useEffect, useState } from 'react';
import { Icons } from '../Icons';
import { useAuthStore } from '../../features/auth/authStore';
import {
  type UserStats,
  loadUserStats,
  claim,
  canClaim,
  previewReward,
} from '../../features/gamification/api';
import { showToast } from '../ui/Toast';
import { haptic } from '../../lib/utils';

export function DailyClaimCard() {
  const user = useAuthStore((s) => s.user);
  const family = useAuthStore((s) => s.family);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !family) return;
    loadUserStats(user.uid, user.name, family.id).then(setStats).catch(() => {});
  }, [user, family]);

  const available = canClaim(stats);
  const preview = previewReward(stats);

  const handleClaim = async () => {
    if (!user || !family) return;
    setLoading(true);
    try {
      const result = await claim(user.uid, user.name, family.id);
      if (result.alreadyClaimed) {
        showToast('Diamenty już odebrane dziś', 'info');
      } else {
        haptic('success');
        showToast(`+${result.reward} 💎 (streak ${result.stats.streak} dni)`, 'success');
      }
      setStats(result.stats);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Nie udało się odebrać', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 rounded-card shadow-card p-4 text-white">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Icons.Gem size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide font-bold opacity-80">Dzisiejszy claim</p>
            <p className="text-lg font-bold tabular-nums">
              {stats?.totalDiamonds ?? 0} 💎 <span className="text-sm font-medium opacity-80">· streak {stats?.streak ?? 0}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClaim}
          disabled={!available || loading}
          className="px-3.5 py-2 rounded-full bg-white text-violet-700 text-sm font-bold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-1.5"
        >
          {available ? (
            <>
              <Icons.Sparkles size={14} />
              +{preview.diamonds} 💎
            </>
          ) : (
            <>
              <Icons.Check size={14} />
              Odebrane
            </>
          )}
        </button>
      </div>
    </div>
  );
}
